import React, { useRef, useState, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_SHOW_DELAY = 200;
const OFFSET_RIGHT = 16;
const OFFSET_LEFT = 8;
// 'bottom-start': distance from the tooltip's left edge to the arrow's centre.
// Keep in sync with `.discord-tooltip--bottom-start::after { left }` in index.css
// so the arrow lands directly under the trigger.
const ARROW_INSET = 16;

export default function Tooltip({
  content,
  children,
  side = 'right',
  delay = DEFAULT_SHOW_DELAY,
  disabled = false,
}) {
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [shown, setShown] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;

    const rect = trigger.getBoundingClientRect();
    const tw = tooltip.offsetWidth;
    const th = tooltip.offsetHeight;

    let top;
    let left;

    if (side === 'right') {
      left = rect.right + OFFSET_RIGHT;
      top = rect.top + rect.height / 2 - th / 2;
    } else if (side === 'left') {
      left = rect.left - tw - OFFSET_LEFT;
      top = rect.top + rect.height / 2 - th / 2;
    } else if (side === 'bottom') {
      left = rect.left + rect.width / 2 - tw / 2;
      top = rect.bottom + OFFSET_RIGHT;
    } else if (side === 'bottom-start') {
      // Left-aligned under the trigger: the arrow (fixed near the tooltip's
      // left edge) points at the trigger's centre, instead of centring the
      // whole box — which for a trigger near the viewport edge would clamp and
      // slide the arrow off-target.
      left = rect.left + rect.width / 2 - ARROW_INSET;
      top = rect.bottom + OFFSET_RIGHT;
    } else {
      left = rect.left + rect.width / 2 - tw / 2;
      top = rect.top - th - OFFSET_RIGHT;
    }

    top = Math.max(8, Math.min(top, window.innerHeight - th - 8));
    left = Math.max(8, Math.min(left, window.innerWidth - tw - 8));

    setCoords({ top, left });
  }, [side]);

  const hide = useCallback(() => {
    clearTimers();
    setShown(false);
    hideTimerRef.current = setTimeout(() => setVisible(false), 120);
  }, [clearTimers]);

  const show = useCallback(() => {
    if (disabled || !content) return;
    clearTimers();
    hideTimerRef.current = null;
    showTimerRef.current = setTimeout(() => {
      setVisible(true);
      requestAnimationFrame(() => setShown(true));
    }, delay);
  }, [clearTimers, content, delay, disabled]);

  useLayoutEffect(() => clearTimers, [clearTimers]);

  useLayoutEffect(() => {
    if (!disabled) return;
    clearTimers();
    setShown(false);
    setVisible(false);
  }, [disabled, clearTimers]);

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [visible, updatePosition, content, side]);

  useLayoutEffect(() => {
    if (visible) updatePosition();
  }, [shown, visible, updatePosition]);

  const child = React.Children.only(children);

  const trigger = React.cloneElement(child, {
    ref: (node) => {
      triggerRef.current = node;
      const { ref } = child;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    onMouseEnter: (e) => {
      child.props.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e) => {
      child.props.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e) => {
      child.props.onFocus?.(e);
      show();
    },
    onBlur: (e) => {
      child.props.onBlur?.(e);
      hide();
    },
    'aria-label': child.props['aria-label'] ?? (typeof content === 'string' ? content : undefined),
  });

  const tooltipEl = (
    <div
      ref={tooltipRef}
      role="tooltip"
      className={`discord-tooltip discord-tooltip--${side}${shown ? ' discord-tooltip--visible' : ''}`}
      style={{ top: coords.top, left: coords.left }}
    >
      {content}
    </div>
  );

  return (
    <>
      {trigger}
      {visible && typeof document !== 'undefined' ? createPortal(tooltipEl, document.body) : null}
    </>
  );
}
